import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, Save, Edit2, Package, Layers, X,
  ArrowUp, ArrowDown, FileText, Factory,
  Info, Globe, RefreshCw, Link2
} from 'lucide-react';
import { CONDITION_OPERATORS, FIELD_TYPES } from './AdminSettings/constants';
import FieldsEditor from './AdminSettings/FieldsEditor';
import StageRulesEditor from './AdminSettings/StageRulesEditor';
import PhoneAdminSettings from './Admin/PhoneAdminSettings';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ActionRow } from '@/components/phone/primitives';

import { Screen, ScreenSplit, Pane, CommandBar } from '@/components/data';

export default function AdminSettingsPage() {
  // Product configs state
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  
  // Edit state
  const [activeTab, setActiveTab] = useState('job_card');
  // which entry of the rail is open: a product type, or one of the shared settings
  const [section, setSection] = useState('product');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [editMode, setEditMode] = useState(null); // 'job_card_fields' | 'stages' | null
  const [editData, setEditData] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // New config dialog
  const [newConfigDialog, setNewConfigDialog] = useState(false);
  const [newConfig, setNewConfig] = useState({ code: '', name: '' });
  
  // Delivery presets state
  const [deliveryPresets, setDeliveryPresets] = useState([]);
  const [editingPresets, setEditingPresets] = useState(false);
  const [newPreset, setNewPreset] = useState({ days: '', label: '' });
  
  // Expanded stage rules
  const [expandedRules, setExpandedRules] = useState({});

  // Production stage registry (category, machine links)
  const [registryStages, setRegistryStages] = useState([]);

  // Global fields state
  const [globalFields, setGlobalFields] = useState([]);
  const [editingGlobalFields, setEditingGlobalFields] = useState(false);
  const [newGlobalField, setNewGlobalField] = useState({ name: '', label: '', type: 'select', options: '', section: 'Finishing' });
  const [syncingField, setSyncingField] = useState(null);

  // Fetch product configs
  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/product-configs');
      setConfigs(res.data);
    } catch (e) {
      toast.error('Failed to load product configurations');
    }
    setLoading(false);
  }, []);
  
  const fetchDeliveryPresets = useCallback(async () => {
    try {
      const res = await api.get('/settings/delivery-presets');
      setDeliveryPresets(res.data.presets || []);
    } catch {
      setDeliveryPresets([
        { days: 3, label: '3 Days' },
        { days: 5, label: '5 Days' },
        { days: 7, label: '1 Week' },
        { days: 10, label: '10 Days' },
        { days: 15, label: '15 Days' },
      ]);
    }
  }, []);

  // Fetch global field templates
  const fetchGlobalFields = useCallback(async () => {
    try {
      const res = await api.get('/admin/global-fields');
      setGlobalFields(res.data.fields || []);
    } catch {
      // silently ignore if endpoint doesn't exist yet
    }
  }, []);

  // The stage registry — what a process becomes on the floor, and who runs it.
  const fetchRegistryStages = useCallback(async () => {
    try {
      const res = await api.get('/stages');
      setRegistryStages(res.data || []);
    } catch {
      setRegistryStages([]);
    }
  }, []);

  useEffect(() => {
    fetchConfigs(); fetchDeliveryPresets(); fetchGlobalFields(); fetchRegistryStages();
  }, [fetchConfigs, fetchDeliveryPresets, fetchGlobalFields, fetchRegistryStages]);

  // Select a config
  const handleSelectConfig = async (config) => {
    try {
      const res = await api.get(`/admin/product-configs/${config.code}`);
      setSelectedConfig(res.data);
      setEditMode(null);
      setEditData([]);
    } catch {
      toast.error('Failed to load configuration');
    }
  };

  // Get all field keys for validation (only job card fields now)
  const allFieldKeys = useMemo(() => {
    if (!selectedConfig) return new Set();
    const jobCardKeys = (selectedConfig.job_card_fields || []).map(f => f.name);
    return new Set([...jobCardKeys]);
  }, [selectedConfig]);

  // ═══════════════════════════════════════════════════
  // FIELD MANAGEMENT (Job Card & Order Creation)
  // ═══════════════════════════════════════════════════

  const startEditFields = (type) => {
    const fields = selectedConfig?.job_card_fields || [];
    setEditData(fields.map(f => ({ ...f, _key: f.id || Math.random().toString() })));
    setEditMode(type);
  };

  const addField = () => {
    const newField = {
      _key: `new-${Date.now()}`,
      name: '',
      label: '',
      type: 'text',
      options: [],
      required: false,
      section: 'Specifications',
      display_order: editData.length,
      auto_filled: false,
      conditional: null,
    };
    setEditData([...editData, newField]);
  };

  const removeField = (idx) => {
    setEditData(editData.filter((_, i) => i !== idx));
  };

  const moveField = (idx, direction) => {
    const newData = [...editData];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newData.length) return;
    [newData[idx], newData[swapIdx]] = [newData[swapIdx], newData[idx]];
    setEditData(newData);
  };

  const updateField = (idx, field, value) => {
    const newData = [...editData];
    newData[idx] = { ...newData[idx], [field]: value };
    // Auto-generate name from label
    if (field === 'label' && !newData[idx].name) {
      newData[idx].name = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
    }
    setEditData(newData);
  };

  const handleSaveFields = async () => {
    if (!selectedConfig || !editMode) return;
    
    // Validate unique keys
    const keys = editData.map(f => f.name).filter(Boolean);
    if (keys.length !== new Set(keys).size) {
      toast.error('Field names must be unique');
      return;
    }
    
    // Check for empty names
    if (editData.some(f => !f.name || !f.label)) {
      toast.error('All fields must have a name and label');
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = `/admin/product-configs/${selectedConfig.code}/job-card-fields`;
      
      const payload = editData.map((f, idx) => ({
        name: f.name,
        label: f.label,
        type: f.type || 'text',
        options: Array.isArray(f.options) ? f.options : (typeof f.options === 'string' ? f.options.split(',').map(s => s.trim()).filter(Boolean) : []),
        required: f.required || false,
        section: f.section || 'Specifications',
        display_order: idx,
        auto_filled: f.auto_filled || false,
        conditional: f.conditional && f.conditional.field ? {
          field: f.conditional.field,
          values: f.conditional.values || [],
          show_when: f.conditional.show_when || 'in',
        } : null,
      }));
      
      await api.put(endpoint, { fields: payload });
      toast.success('Fields saved successfully');
      
      // Refresh config
      await handleSelectConfig(selectedConfig);
      setEditMode(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save fields');
    }
    setSaving(false);
  };

  // ═══════════════════════════════════════════════════
  // STAGE RULES MANAGEMENT
  // ═══════════════════════════════════════════════════

  const startEditStages = () => {
    const rules = selectedConfig?.stage_generation_rules || [];
    setEditData(rules.map(r => ({ 
      ...r, 
      _key: r.id || Math.random().toString(),
      conditions: r.conditions || [],
      // Convert null/empty machine_man_type to '_none' for Select component
      machine_man_type: r.machine_man_type || '_none'
    })));
    setEditMode('stages');
  };

  const addStage = () => {
    const newRule = {
      _key: `new-${Date.now()}`,
      stage_name: '',
      stage_key: '',
      default_sequence: (editData.length + 1) * 10,
      conditions: [],
      machine_man_type: '',
    };
    setEditData([...editData, newRule]);
  };

  const removeStage = (idx) => {
    setEditData(editData.filter((_, i) => i !== idx));
  };

  const moveStage = (idx, direction) => {
    const newData = [...editData];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newData.length) return;
    [newData[idx], newData[swapIdx]] = [newData[swapIdx], newData[idx]];
    // Update sequences
    newData.forEach((r, i) => { r.default_sequence = (i + 1) * 10; });
    setEditData(newData);
  };

  const updateStage = (idx, field, value) => {
    const newData = [...editData];
    newData[idx] = { ...newData[idx], [field]: value };
    // Auto-generate stage_key from stage_name
    if (field === 'stage_name' && !newData[idx].stage_key) {
      newData[idx].stage_key = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '');
    }
    setEditData(newData);
  };

  // Condition management
  const addCondition = (stageIdx) => {
    const newData = [...editData];
    newData[stageIdx].conditions = [
      ...newData[stageIdx].conditions,
      { field: '', operator: 'not_empty', value: '', include_when_empty: false }
    ];
    setEditData(newData);
  };

  const removeCondition = (stageIdx, condIdx) => {
    const newData = [...editData];
    newData[stageIdx].conditions = newData[stageIdx].conditions.filter((_, i) => i !== condIdx);
    setEditData(newData);
  };

  const updateCondition = (stageIdx, condIdx, field, value) => {
    const newData = [...editData];
    newData[stageIdx].conditions[condIdx] = {
      ...newData[stageIdx].conditions[condIdx],
      [field]: value
    };
    setEditData(newData);
  };

  const handleSaveStages = async () => {
    if (!selectedConfig) return;
    
    // Validate unique stage keys
    const keys = editData.map(r => r.stage_key).filter(Boolean);
    if (keys.length !== new Set(keys).size) {
      toast.error('Stage keys must be unique');
      return;
    }
    
    // Check for empty names
    if (editData.some(r => !r.stage_name || !r.stage_key)) {
      toast.error('All stages must have a name and key');
      return;
    }
    
    // Validate conditions reference existing fields
    for (const rule of editData) {
      for (const cond of rule.conditions || []) {
        if (cond.field && !allFieldKeys.has(cond.field)) {
          toast.error(`Stage "${rule.stage_name}" references unknown field "${cond.field}"`);
          return;
        }
      }
    }
    
    setSaving(true);
    try {
      const payload = editData.map((r, idx) => ({
        stage_name: r.stage_name,
        stage_key: r.stage_key,
        default_sequence: r.default_sequence || (idx + 1) * 10,
        supports_external_vendor: r.supports_external_vendor || false,
        conditions: (r.conditions || []).map(c => {
          const isArrayOp = CONDITION_OPERATORS.find(o => o.value === c.operator)?.isArray;
          let finalValue = c.value;
          if (isArrayOp && typeof finalValue === 'string') {
            finalValue = finalValue.split(',').map(s => s.trim()).filter(Boolean);
          }
          return {
            field: c.field,
            operator: c.operator,
            value: finalValue || '',
            include_when_empty: c.include_when_empty || false,
          };
        }),
        // Convert '_none' back to null for API
        machine_man_type: r.machine_man_type === '_none' ? null : (r.machine_man_type || null),
      }));
      
      const res = await api.put(`/admin/product-configs/${selectedConfig.code}/stage-rules`, { rules: payload });
      
      let msg = 'Stage rules saved successfully';
      if (res.data.stages_added?.length > 0) {
        msg += `. Added: ${res.data.stages_added.join(', ')}`;
      }
      if (res.data.stages_removed?.length > 0) {
        msg += `. Removed: ${res.data.stages_removed.join(', ')}`;
      }
      toast.success(msg);
      
      await handleSelectConfig(selectedConfig);
      setEditMode(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save stage rules');
    }
    setSaving(false);
  };

  // ═══════════════════════════════════════════════════
  // CREATE NEW PRODUCT CONFIG
  // ═══════════════════════════════════════════════════

  const handleCreateConfig = async () => {
    if (!newConfig.code || !newConfig.name) {
      toast.error('Code and name are required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/admin/product-configs', {
        code: newConfig.code.toUpperCase(),
        name: newConfig.name,
      });
      toast.success(`Product type "${res.data.name}" created`);
      setNewConfigDialog(false);
      setNewConfig({ code: '', name: '' });
      fetchConfigs();
      handleSelectConfig(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create product type');
    }
    setSaving(false);
  };

  // ═══════════════════════════════════════════════════
  // DELIVERY PRESETS
  // ═══════════════════════════════════════════════════

  const saveDeliveryPresets = async () => {
    setSaving(true);
    try {
      await api.put('/settings/delivery-presets', { presets: deliveryPresets });
      toast.success('Delivery presets saved');
      setEditingPresets(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save presets');
    }
    setSaving(false);
  };
  
  const addPreset = () => {
    if (!newPreset.days || !newPreset.label) {
      toast.error('Both days and label are required');
      return;
    }
    setDeliveryPresets(prev => [...prev, { days: parseInt(newPreset.days), label: newPreset.label }]);
    setNewPreset({ days: '', label: '' });
  };
  
  const removePreset = (index) => {
    setDeliveryPresets(prev => prev.filter((_, i) => i !== index));
  };
  
  const movePreset = (index, direction) => {
    const newPresets = [...deliveryPresets];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newPresets.length) return;
    [newPresets[index], newPresets[newIndex]] = [newPresets[newIndex], newPresets[index]];
    setDeliveryPresets(newPresets);
  };

  // ═══════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div></div>;
  }

  const SHARED = [
    { key: 'presets', label: 'Delivery presets', icon: Layers },
    { key: 'globals', label: 'Global field templates', icon: Globe },
  ];

  const railButton = (on, onClick, Icon, label, meta, testId) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      aria-pressed={on}
      data-testid={testId}
      className={`flex w-full items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left transition-colors last:border-b-0 ${
        on ? 'bg-brand-blue/10 font-semibold text-brand-blue' : 'text-slate-600 hover:bg-slate-50'
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px]">{label}</span>
        {meta && <span className="block truncate text-[10px] font-normal text-slate-400">{meta}</span>}
      </span>
    </button>
  );

  // The section bodies are shared by the desktop split and the phone shell, so
  // both edit exactly the same thing; only the frame around them differs.
  const presetsBody = (
    <div className="max-w-xl space-y-1">
      {deliveryPresets.map((preset, idx) => (
        <div key={idx} className="flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1">
          <Badge variant="outline" className="text-[10px] tabular-nums">{preset.days} days</Badge>
          <span className="flex-1 truncate text-[13px] text-slate-700">{preset.label}</span>
          {editingPresets && (
            <span className="flex gap-0.5">
              <Button size="sm" variant="ghost" className="h-11 w-11 p-0 lg:h-6 lg:w-6"
                onClick={() => movePreset(idx, 'up')} disabled={idx === 0} aria-label="Move up">
                <ArrowUp className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-11 w-11 p-0 lg:h-6 lg:w-6"
                onClick={() => movePreset(idx, 'down')} disabled={idx === deliveryPresets.length - 1} aria-label="Move down">
                <ArrowDown className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-11 w-11 p-0 lg:h-6 lg:w-6"
                onClick={() => removePreset(idx)} aria-label="Remove preset">
                <X className="h-3 w-3 text-red-500" />
              </Button>
            </span>
          )}
        </div>
      ))}
      {deliveryPresets.length === 0 && (
        <p className="py-6 text-center text-xs text-slate-400">No presets yet</p>
      )}

      {editingPresets && (
        <div className="mt-2 flex gap-2 rounded border border-dashed border-slate-300 p-2">
          <Input type="number" placeholder="Days" value={newPreset.days}
            onChange={e => setNewPreset(p => ({ ...p, days: e.target.value }))}
            className="h-11 w-24 text-sm lg:h-8" />
          <Input placeholder="Label (e.g. '1 Week')" value={newPreset.label}
            onChange={e => setNewPreset(p => ({ ...p, label: e.target.value }))}
            className="h-11 flex-1 text-sm lg:h-8" />
          <Button size="sm" onClick={addPreset} variant="outline" className="h-11 w-11 lg:h-8 lg:w-auto" aria-label="Add preset">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );

  const globalsBody = (
    <div className="space-y-1">
      {globalFields.length === 0 ? (
        <p className="py-8 text-center text-xs text-slate-400">
          No global field templates yet. They keep options and type in step across product types.
        </p>
      ) : globalFields.map((gf) => (
        <div key={gf.name} className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1.5">
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <Link2 className="h-3.5 w-3.5 shrink-0 text-brand-blue" />
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-[13px] font-medium text-slate-800">{gf.label}</span>
                <Badge variant="outline" className="font-mono text-[10px]">{gf.name}</Badge>
                <Badge variant="outline" className="text-[10px]">{gf.type}</Badge>
                <span className="text-[10px] text-slate-400">{gf.section}</span>
              </span>
              {gf.options?.length > 0 && (
                <span className="mt-0.5 block truncate text-[10px] text-slate-400">
                  {gf.options.join(', ')}
                </span>
              )}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <Button
              size="sm" variant="ghost" className="h-11 px-3 text-[11px] text-brand-blue lg:h-6 lg:px-1.5"
              disabled={syncingField === gf.name}
              onClick={async () => {
                setSyncingField(gf.name);
                try {
                  const res = await api.get(`/admin/global-fields/usage/${gf.name}`);
                  const usage = res.data.usage || [];
                  if (usage.length === 0) toast.info(`"${gf.label}" is not used in any product type`);
                  else toast.success(`"${gf.label}" is used in ${usage.length} product type(s): ${usage.map(u => u.code).join(', ')}`);
                } catch { toast.error('Failed to check usage'); }
                setSyncingField(null);
              }}
            >
              <Info className="mr-1 h-3 w-3" /> Usage
            </Button>
            <Button
              size="sm" variant="ghost" className="h-11 px-3 text-[11px] text-green-600 lg:h-6 lg:px-1.5"
              disabled={syncingField === gf.name}
              onClick={async () => {
                setSyncingField(gf.name);
                try {
                  const res = await api.put(`/admin/global-fields/${gf.name}`, {
                    label: gf.label, type: gf.type, options: gf.options, section: gf.section,
                  });
                  const propagated = res.data.propagated_to || [];
                  if (propagated.length > 0) toast.success(`Synced to ${propagated.length} product type(s): ${propagated.join(', ')}`);
                  else toast.info('No product types are using this field');
                  if (selectedConfig && propagated.includes(selectedConfig.code)) {
                    await handleSelectConfig(selectedConfig);
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to sync field');
                }
                setSyncingField(null);
              }}
            >
              <RefreshCw className={`mr-1 h-3 w-3 ${syncingField === gf.name ? 'animate-spin' : ''}`} /> Sync all
            </Button>
          </span>
        </div>
      ))}

      <div className="mt-3 rounded border border-dashed border-slate-300 p-2">
        <p className="mb-1.5 text-[11px] font-medium text-slate-600">Add a global field</p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-[120px] flex-1">
            <span className="text-[10px] text-slate-500">Field key</span>
            <Input value={newGlobalField.name} className="h-11 text-xs lg:h-8"
              placeholder="e.g. lamination_type"
              onChange={e => setNewGlobalField(p => ({ ...p, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} />
          </label>
          <label className="min-w-[120px] flex-1">
            <span className="text-[10px] text-slate-500">Label</span>
            <Input value={newGlobalField.label} className="h-11 text-xs lg:h-8"
              placeholder="e.g. Lamination Type"
              onChange={e => setNewGlobalField(p => ({ ...p, label: e.target.value }))} />
          </label>
          <label className="w-full lg:w-[110px]">
            <span className="text-[10px] text-slate-500">Type</span>
            <Select value={newGlobalField.type} onValueChange={v => setNewGlobalField(p => ({ ...p, type: v }))}>
              <SelectTrigger className="h-11 text-xs lg:h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value} className="text-xs">{ft.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="min-w-[150px] flex-1">
            <span className="text-[10px] text-slate-500">Options (comma-separated)</span>
            <Input value={newGlobalField.options} className="h-11 text-xs lg:h-8"
              placeholder="Option1, Option2"
              onChange={e => setNewGlobalField(p => ({ ...p, options: e.target.value }))} />
          </label>
          <Button
            size="sm" variant="outline" className="h-11 w-full lg:h-8 lg:w-auto"
            aria-label="Add global field"
            onClick={async () => {
              if (!newGlobalField.name || !newGlobalField.label) {
                toast.error('Field key and label are required');
                return;
              }
              try {
                await api.post('/admin/global-fields', {
                  name: newGlobalField.name,
                  label: newGlobalField.label,
                  type: newGlobalField.type,
                  options: newGlobalField.options
                    ? newGlobalField.options.split(',').map(s => s.trim()).filter(Boolean) : [],
                  section: newGlobalField.section,
                });
                toast.success(`Global field "${newGlobalField.label}" created`);
                setNewGlobalField({ name: '', label: '', type: 'select', options: '', section: 'Finishing' });
                fetchGlobalFields();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to create global field');
              }
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const stagesEditor = selectedConfig && (
    <StageRulesEditor
      rules={editMode === 'stages' ? editData : selectedConfig.stage_generation_rules || []}
      editMode={editMode === 'stages'}
      allFieldKeys={allFieldKeys}
      expandedRules={expandedRules}
      setExpandedRules={setExpandedRules}
      onStartEdit={startEditStages}
      onCancel={() => setEditMode(null)}
      onSave={handleSaveStages}
      onAdd={addStage}
      onRemove={removeStage}
      onMove={moveStage}
      onUpdate={updateStage}
      onAddCondition={addCondition}
      onRemoveCondition={removeCondition}
      onUpdateCondition={updateCondition}
      saving={saving}
      registryStages={registryStages}
    />
  );

  const fieldsEditor = selectedConfig && (
    <FieldsEditor
      fields={editMode === 'job_card_fields' ? editData : selectedConfig.job_card_fields || []}
      editMode={editMode === 'job_card_fields'}
      onStartEdit={() => startEditFields('job_card_fields')}
      onCancel={() => setEditMode(null)}
      onSave={handleSaveFields}
      onAdd={addField}
      onRemove={removeField}
      onMove={moveField}
      onUpdate={updateField}
      saving={saving}
      title="Job Card Fields"
      description="Fields that appear on the job card for designers and production staff to fill."
      allFieldNames={Array.from(allFieldKeys)}
    />
  );

  const createDialog = (
    <Dialog open={newConfigDialog} onOpenChange={setNewConfigDialog}>
      <DialogContent data-testid="new-config-dialog">
        <DialogHeader><DialogTitle>Create new product type</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-sm font-medium">Code * (2–5 characters)</Label>
            <Input
              value={newConfig.code}
              onChange={(e) => setNewConfig(p => ({ ...p, code: e.target.value.toUpperCase().slice(0, 5) }))}
              placeholder="e.g. STN"
              maxLength={5}
              className="h-11 lg:h-9"
              data-testid="new-config-code"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Name *</Label>
            <Input
              value={newConfig.name}
              onChange={(e) => setNewConfig(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Stationery"
              className="h-11 lg:h-9"
              data-testid="new-config-name"
            />
          </div>
          <p className="text-xs text-slate-400">
            A default Production → Packing sequence is created; you can change it straight after.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setNewConfigDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateConfig} disabled={saving}
            className="bg-brand-blue text-white hover:bg-brand-blue-dark" data-testid="confirm-create-config">
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // two screens below lg: the list, then the editor for what was tapped
  if (isMobile) {
    const presetActions = !editingPresets
      ? [{ key: 'edit', label: 'Edit presets', icon: Edit2, onClick: () => setEditingPresets(true), testID: 'edit-presets-btn' }]
      : [
        { key: 'save', label: saving ? 'Saving…' : 'Save', icon: Save, primary: true, disabled: saving,
          onClick: saveDeliveryPresets, testID: 'save-presets-btn' },
        { key: 'cancel', label: 'Cancel', icon: X, onClick: () => { setEditingPresets(false); fetchDeliveryPresets(); } },
      ];
    return (
      <>
        <PhoneAdminSettings
          configs={configs}
          selectedConfig={selectedConfig}
          section={section}
          deliveryPresets={deliveryPresets}
          globalFields={globalFields}
          activeTab={activeTab}
          onTab={setActiveTab}
          onOpenConfig={(config) => { setSection('product'); handleSelectConfig(config); }}
          onOpenShared={setSection}
          onBack={() => { setSelectedConfig(null); setSection('product'); setEditMode(null); setEditData([]); }}
          onNew={() => setNewConfigDialog(true)}
          onStages={() => navigate('/admin-settings/stages')}
          onAttachments={() => navigate('/admin-settings/attachment-categories')}
        >
          {section === 'presets' ? (
            <>
              <ActionRow actions={presetActions} testID="preset-actions" />
              {presetsBody}
            </>
          ) : section === 'globals' ? globalsBody
            : activeTab === 'stages' ? stagesEditor : fieldsEditor}
        </PhoneAdminSettings>
        {createDialog}
      </>
    );
  }

  return (
    <Screen data-testid="admin-settings-page">
      <CommandBar
        title="Settings"
        subtitle={
          section === 'presets' ? 'Delivery presets'
            : section === 'globals' ? 'Global field templates'
              : selectedConfig ? `${selectedConfig.name} (${selectedConfig.code})` : 'Pick a product type'
        }
        actions={[
          { key: 'stages', label: 'Production Stages', icon: Factory, onClick: () => navigate('/admin-settings/stages') },
          { key: 'attachments', label: 'Attachment Categories', icon: FileText, onClick: () => navigate('/admin-settings/attachment-categories') },
          { key: 'new', label: 'New Product Type', icon: Plus, tone: 'accent', onClick: () => setNewConfigDialog(true) },
        ]}
      />

      <ScreenSplit
        railWidth="16rem"
        rail={
          <Pane title="Settings" aside={`${configs.length} product types`} className="max-h-full">
            <p className="border-b border-slate-100 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Product types
            </p>
            {configs.map((config) => railButton(
              section === 'product' && selectedConfig?.code === config.code,
              () => { setSection('product'); handleSelectConfig(config); },
              Package,
              config.name,
              `${config.code} · ${config.stage_rules_count || 0} stages · ${config.job_card_fields_count || 0} fields`,
              `config-${config.code}`,
            ))}

            <p className="border-b border-slate-100 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Shared across products
            </p>
            {SHARED.map(item => railButton(
              section === item.key,
              () => setSection(item.key),
              item.icon,
              item.label,
              item.key === 'globals' ? `${globalFields.length} field${globalFields.length === 1 ? '' : 's'}`
                : `${deliveryPresets.length} preset${deliveryPresets.length === 1 ? '' : 's'}`,
            ))}
          </Pane>
        }
      >
        {section === 'presets' ? (
          <Pane
            title="Delivery date presets"
            aside="the quick-select options on the order screen"
            bodyClassName="p-3"
            actions={!editingPresets ? (
              <button type="button" onClick={() => setEditingPresets(true)}
                className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-blue-100 hover:bg-white/20">
                Edit
              </button>
            ) : (
              <span className="flex gap-1">
                <button type="button" onClick={() => { setEditingPresets(false); fetchDeliveryPresets(); }}
                  className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-blue-100 hover:bg-white/20">
                  Cancel
                </button>
                <button type="button" onClick={saveDeliveryPresets} disabled={saving}
                  className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-white hover:bg-white/30 disabled:opacity-40">
                  Save
                </button>
              </span>
            )}
          >
            {presetsBody}
          </Pane>
        ) : section === 'globals' ? (
          <Pane
            title="Global field templates"
            aside="shared definitions — editing one updates every product type that uses it"
            bodyClassName="p-3"
          >
            {globalsBody}
          </Pane>
        ) : !selectedConfig ? (
          <Pane title="Product type" bodyClassName="p-3">
            <div className="py-10 text-center">
              <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">Pick a product type from the left to configure it.</p>
              <p className="mx-auto mt-3 max-w-md text-xs leading-relaxed text-slate-400">
                Each one is set up in two places, in the order the work happens: the fields the job card
                asks production to fill in, and the stages those imply. Changes apply to new orders only.
              </p>
            </div>
          </Pane>
        ) : (
          <Pane
            title={selectedConfig.name}
            aside={selectedConfig.code}
            scroll={false}
            bodyClassName="flex min-h-0 flex-col"
          >
            {/* Two genuinely different shapes of the same product type, so this
                is one of the few places tabs still earn their keep. */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
              <TabsList className="mx-3 mt-2 w-fit shrink-0">
                <TabsTrigger value="job_card" className="text-xs">
                  <FileText className="mr-1 h-3.5 w-3.5" />Job card fields
                </TabsTrigger>
                <TabsTrigger value="stages" className="text-xs">
                  <Factory className="mr-1 h-3.5 w-3.5" />Stage rules
                </TabsTrigger>
              </TabsList>

              <div className="min-h-0 flex-1 overflow-y-auto p-3">
                <TabsContent value="stages" className="mt-0">{stagesEditor}</TabsContent>
                <TabsContent value="job_card" className="mt-0">{fieldsEditor}</TabsContent>
              </div>
            </Tabs>
          </Pane>
        )}
      </ScreenSplit>

      {createDialog}
    </Screen>
  );
}
